import * as angular from 'angular';
import * as _ from 'lodash';

import { KeyValuePair, NameBuilderConfiguration, IBindable, Group, TagDefinition, IValueViewModel, TagDateValue, Dictionary, VariableType, SeriesSelectorViewModel } from '../../models';
import { GroupDto } from '../../models/DTO/GroupDto';
import { SelectorType, ObjectType, ActualDataType } from '../../models/enum';
import { errorTextColor, guidEmpty, missingId } from '../../common/constants';
import { SeriesConfiguration } from '../../widgets';
import { IGroupService, IGroupSelectionService, IIdentifierVmService, IRequestService, INameBuilderService, IFormatNumberHelper, IRelativePathHelper, IStringValuesHelperService } from '../';
import { TrendSeries } from './TrendSeries';
import { getPopoverCallback } from '../../widgets/common/callback';
import { popoverItem, toDictionary } from '../../common';
import { IDigitalSetHelperService } from '../../services/helpers/digitalSetHelper.service';
import { IPresentationUnitConfiguration } from '../../widgets/models/interfaces';

export interface IBindingResult {
    map: { [guid: string]: string };
    errors: { [guid: string]: IBindingResultError };
}

export interface IBindingResultError {
    newPath: string;
    currentPath: string;
    relative: string;
    vm?: IValueViewModel;
    group?: Group;
}

export interface ISeriesService {
    createSeriesList<T = any>(
        seriesConfiguration: SeriesConfiguration[],
        nameConfiguration: NameBuilderConfiguration,
        viewModels: Dictionary<IValueViewModel>
    ): angular.IPromise<TrendSeries<T>[]>;
    getUpdatedGuids<T extends IBindable>(configuration: T[]): angular.IPromise<T[]>;
    bind(serie: any, groupPath: string): void;
    getMinMax(tagDefinition: TagDefinition, vm: IValueViewModel, groups: Dictionary<Group>): angular.IPromise<{ min: number; max: number; defaultValue: number }>;
    createMissingSerie(): any;
    updateMissingSeries(missing: KeyValuePair<string, any[]>[]): any[];

    getName(config: SeriesConfiguration, vm: IValueViewModel, nameConfiguration?: NameBuilderConfiguration): string;
    createSeries(t: any, name: string, vm: IValueViewModel, hasXAxis?:boolean): any;
    bindToReference(series: string[], fromGuid: string, toGuid: string): angular.IPromise<IBindingResult>;
    bindSeriesToReference(series: string[], viewModels: Dictionary<IValueViewModel>, groups: Dictionary<Group>, fromGroup: Group, toGroup: Group): IBindingResult;
    convertUnit(vm: IValueViewModel, configuration: IPresentationUnitConfiguration, variablesTypes: Dictionary<VariableType>): string;
}

interface IBoundIdentifier {
    originalGuid: string;
    guid: string;
    name: string;
    groupGuid: string;
    groupName: string;
}

export class SeriesService implements ISeriesService {
    static serviceId = 'seriesService';
    static $inject = [
        '$q',
        '$translate',
        '$state',
        'groupService',
        'groupSelectionService',
        'identifierVmService',
        'requestService',
        'nameBuilderService',
        'formatNumberHelper',
        'relativePathHelper',
        'digitalSetHelperService',
        'stringValuesHelperService'
    ];

    constructor(
        private $q: angular.IQService,
        private _$translate: angular.translate.ITranslateService,
        private _$state: angular.ui.IStateService,
        private _groupService: IGroupService,
        private _groupSelectionService: IGroupSelectionService,
        private _identifierVmService: IIdentifierVmService,
        private _requestService: IRequestService,
        private _nameBuilderService: INameBuilderService,
        private _formatNumberHelper: IFormatNumberHelper,
        private _relativePathHelper: IRelativePathHelper,
        private _digitalSetHelperService: IDigitalSetHelperService,
        private _stringValuesHelperService: IStringValuesHelperService
    ) {
    }

    createSeriesList(
        seriesConfiguration: SeriesConfiguration[],
        nameConfiguration: NameBuilderConfiguration,
        viewModels: Dictionary<IValueViewModel>
    ): angular.IPromise<any> {
        try {
            const areaFilter = seriesConfiguration
                ? seriesConfiguration.filter(s => s.seriesType !== 'area')
                : [];
            const constantFilter = areaFilter.filter(s => s.type !== SelectorType.constant);
            const vvms = constantFilter.map(d => viewModels[d.guid])
                .filter(d => !!d);
            const mapped = this.mapFromViewModels(vvms, constantFilter, areaFilter, nameConfiguration);
            return this.$q.when(mapped);
        } catch (e) {
            console.error(e);
            return this.$q.when({});
        }
    }

    private mapFromViewModels(data: IValueViewModel[],
        constantFilter: SeriesConfiguration[],
        areaFilter: SeriesConfiguration[],
        nameConfiguration?: NameBuilderConfiguration
    ) {
        const fromServer = constantFilter.map(c => {
            const vm = _.find(data, d => c.guid === d.guid);
            if (vm) {
                const name = this.getName(c, vm, nameConfiguration);
                return this.createSeries(c, name, vm);
            } else if (c.rawInformation) {
                const name = c.rawInformation.tagName;
                return this.createHistorianSeries(c, name);
            } else {
                const name = '';
                return this.createUnknownSeries(c, name);
            }
        });
        const items = areaFilter.filter(a => a.type === SelectorType.constant)
            .map(a => {
                const res = a;
                res.name = '';
                return res;
            });
        const result = fromServer
            .filter(d => !!d && !!d.identifier)
            .concat(items);
        return result;
    }

    getUpdatedGuids<T extends IBindable>(configuration: T[]): angular.IPromise<T[]> {
        const result = configuration.map(c => Object.assign({}, c));
        const group = this._groupSelectionService.selectedGroup();
        if (!group) {
            return this.$q.when(result);
        }
        const defered = this.$q.defer<T[]>();
        const boundSeries = result.filter(s => s.isBoundToGroup);
        if (boundSeries.length > 0 && group) {

            boundSeries
                .filter(b => b.guid === guidEmpty)
                .forEach(b => b.guid = b.originalGuid);
            const boundConfiguration = {
                groupGuid: group.guid,
                originalGuidList: boundSeries.map(d => {
                    return {
                        guid: d.guid,
                        path: d.relativePath
                    };
                })
            };
            this._requestService
                .cachedPostAtV1<IBoundIdentifier[]>('identifier/updatedIdentifiers', boundConfiguration)
                .then(data => {
                    boundSeries.forEach(bs => {
                        bs.originalGuid = bs.guid;
                        const found = _.find(data, d => d.originalGuid === bs.guid);
                        bs.guid = found?.guid ?? guidEmpty;
                    });
                    defered.resolve(result);
                })
                .catch(reason => defered.reject(reason));
        } else {
            defered.resolve(result);
        }
        return defered.promise;
    }

    // This method is probably obsolete
    // HS: Probably still used in validation page
    bind(serie: any, groupPath: string): void {
        const selectedGroup = this._groupSelectionService.selectedGroup();
        if (selectedGroup) {
            const isIdentifier = (serie.type === SelectorType.identifier || serie.objectType === ObjectType.Identifier);
            serie.isBoundToGroup = serie.isBoundToGroup ||
                (isIdentifier && _.some(selectedGroup.identifierCollection, i => i.guid === serie.guid));
            const absolutePath = (selectedGroup as any).absolutePath || (selectedGroup as any).absoluteName;
            const replace = groupPath ? groupPath.replace(absolutePath, '.') : null;
            serie.relativePath = replace;
        }
    }

    bindToReference(series: string[], fromGuid: string, toGuid: string): angular.IPromise<IBindingResult> {
        const defered = this.$q.defer<IBindingResult>();

        if (series.length === 0) {
            defered.resolve({ errors: {}, map: {} });
        } else {
            this.$q
                .all([
                    this._groupService.getAll(),
                    this._identifierVmService.getAllViewModels()
                ])
                .then(all => {
                    const groups = toDictionary((all[0] as GroupDto[]).map(dto => dto.group));
                    const fromGroup = groups[fromGuid];
                    const toGroup = groups[toGuid];
                    const vmList = toDictionary(all[1]) as Dictionary<IValueViewModel>;
                    const result = this.bindSeriesToReference(series, vmList, groups, fromGroup, toGroup);
                    defered.resolve(result);
                })
                .catch(reason => defered.reject(reason));
        }

        return defered.promise;
    }

    bindSeriesToReference(series: string[], viewModels: Dictionary<IValueViewModel>, groups: Dictionary<Group>, fromGroup: Group, toGroup: Group): IBindingResult {
        const map: { [guid: string]: string } = {};
        const err: { [guid: string]: IBindingResultError } = {};

        const groupList = _.values(groups);
        const vmList = _.values(viewModels);
        series.sort();
        series.forEach(guid => {
            const vm = viewModels[guid];
            if (vm) {
                this.seekRelativeIdentifier(vm, vmList, fromGroup, toGroup, map, err);
            } else {
                const group = groups[guid];
                if (group) {
                    this.seekRelativeGroup(group, groupList, fromGroup, toGroup, map, err);
                } else {
                    err[guid] = { currentPath: 'unknown', newPath: '', relative: '' };
                }
            }

        });
        return { errors: err, map: map };
    }

    private seekRelativeIdentifier(
        vm: IValueViewModel,
        viewModels: IValueViewModel[],
        fromGroup: Group,
        toGroup: Group,
        map: { [guid: string]: string },
        err: { [guid: string]: IBindingResultError }
    ) {
        const relative = this._relativePathHelper.getRelativePath(fromGroup.absoluteName, vm.groupAbsolutePath);
        const newPath = this._relativePathHelper.resolveRelativePath(`${toGroup.absoluteName}/${relative}`);
        const found = _.find(viewModels, v => v.groupAbsolutePath === newPath && v.name === vm.name);

        if (found) {
            map[vm.guid] = found.guid;
        } else {
            map[vm.guid] = null; // should we?
            err[vm.guid] = {
                currentPath: vm.groupAbsolutePath,
                newPath: newPath,
                relative: relative,
                vm: vm
            };
        }
    }

    private seekRelativeGroup(
        group: Group,
        groupList: Group[],
        fromGroup: Group,
        toGroup: Group,
        map: { [guid: string]: string },
        err: { [guid: string]: IBindingResultError }
    ) {
        const relative = this._relativePathHelper.getRelativePath(fromGroup.absoluteName, group.absoluteName);
        const newPath = this._relativePathHelper.resolveRelativePath(`${toGroup.absoluteName}/${relative}`);
        const found = _.find(groupList, v => v.absoluteName === newPath && (!relative ? v.name === toGroup.name : true));

        if (found) {
            map[group.guid] = found.guid;
        } else {
            map[group.guid] = null; // should we?
            err[group.guid] = {
                currentPath: group.absoluteName,
                newPath: newPath,
                relative: relative,
                group: group
            };
        }
    }

    getName(
        config: SeriesConfiguration,
        vm: IValueViewModel,
        nameConfiguration?: NameBuilderConfiguration
    ): string {
        let name: string;
        if (!nameConfiguration) {
            let name1 = `${vm.name}`;
            if (vm.unitName) {
                name1 = `${name1} [${vm.unitName}]`;
            }
            name = name1;
        } else {
            nameConfiguration.hasUnit = nameConfiguration.hasUnit === undefined || nameConfiguration.hasUnit;
            name = this._nameBuilderService.getName(vm, nameConfiguration, config);
        }
        return name;
    }

    createSeries(sc: SeriesConfiguration, name: string, vm: IValueViewModel, hasXAxis = true): any {
        const decimals = this.getDecimals(sc.decimals);
        const crosshairFormat = (vm.actualDataType === ActualDataType.String && this._stringValuesHelperService.isConvertibleToStringValue(sc.value))
            || (vm.actualDataType === ActualDataType.Digital && this._digitalSetHelperService.isConvertibleToDigitalValue(sc.value))
            ? (d: TagDateValue) => d.comment
            : (d: TagDateValue) => this._formatNumberHelper.getFormatter(decimals).format(d.value);
        try {
            const popover = vm.objectType === ObjectType.Tag
                ? 'tagPopover.html'
                : 'identifierPopover.html';
            const legendAttributes: any = {
                'wt-popover-svg': 'true',
                item: popoverItem(vm),
                'template': popover,
                'compile': 'wt-popover-svg',
                placement: 'left'
            };
            const newSeries = {
                name: name,
                legendText: name,
                isVisible: true,
                identifier: sc.value + vm.guid.toLowerCase() + ((sc.offset && sc.offset.interval) ? (sc.offset.interval.toString() + sc.offset.intervalTimeUnit.toString()) : ''),
                identifierType: vm.identifierType,
                color: sc.color,
                value: (d: TagDateValue) => d.value,
                selector: (d: TagDateValue) => {
                    let id = d.valueType;
                    if (d.guid) {
                        id += d.guid.toLowerCase();
                    }
                    if (!!d.offset && !!d.offset.interval) {
                        id += (d.offset.interval.toString() + d.offset.intervalTimeUnit.toString());
                    }
                    return id;
                },
                symbol: sc.symbol,
                isSymbolVisible: !!sc.isSymbolVisible || ((sc as any).isXAxis === false && hasXAxis), // trick for x axis of scatter plot
                isLineVisible: !!sc.isLineVisible,
                lineStyle: (sc as any).lineStyle,
                isBarsVisible: !!sc.isBarsVisible,
                isValueLabelsVisible: sc.isValueLabelsVisible,
                isStacked: sc.isStacked,
                coloringConditions: sc.coloringConditions ? sc.coloringConditions : [],
                hasOwnAxis: !!sc.hasOwnAxis,
                yMin: (sc.yMin || sc.yMin === 0) ? sc.yMin : (sc.isClipped ? vm.min : null),
                yMax: (sc.yMax || sc.yMax === 0) ? sc.yMax : (sc.isClipped ? vm.max : null),
                pane: sc.pane ? sc.pane : 0,
                crosshairFormat: crosshairFormat,
                hasReconciledValue: vm.hasReconciledValue,
                isConstraint: vm.isConstraint,
                attributes: legendAttributes,
                objectType: vm.objectType,
                click: getPopoverCallback(vm, this._$state.go),
                isXAxis: (sc as any).isXAxis,
                renderingMode: sc.renderingMode,
                serieState: (sc.serieState as any) === true
                    ? 1
                    : (sc.serieState as any) === false
                        ? 0
                        : sc.serieState,
                decimals // For change detection purpose
            };
            return newSeries;
        } catch (e) {
            console.error(e);
            return {};
        }
    }

    private createHistorianSeries(sc: SeriesConfiguration, name: string): any {
        const decimals = this.getDecimals(sc.decimals);
        const crosshairFormat = (this._stringValuesHelperService.isConvertibleToStringValue(sc.value))
            ? (d: TagDateValue) => d.comment
            : (d: TagDateValue) => this._formatNumberHelper.getFormatter(decimals).format(d.value);
        const newSeries = {
            name: name,
            legendText: name,
            isVisible: true,
            identifier: sc.value + sc.guid.toLowerCase(),
            color: sc.color,
            value: (d: TagDateValue) => d.value,
            selector: (d: TagDateValue) => {
                let id = d.valueType;
                if (d.guid) {
                    id += d.guid.toLowerCase();
                }
                return id;
            },
            symbol: sc.symbol,
            isSymbolVisible: !!sc.isSymbolVisible || (sc.isXAxis === false), // trick for x axis of scatter plot
            isLineVisible: !!sc.isLineVisible,
            lineStyle: (sc as any).lineStyle,
            isBarsVisible: !!sc.isBarsVisible,
            isValueLabelsVisible: sc.isValueLabelsVisible,
            isStacked: sc.isStacked,
            coloringConditions: sc.coloringConditions ? sc.coloringConditions : [],
            hasOwnAxis: !!sc.hasOwnAxis,
            yMin: (sc.yMin || sc.yMin === 0) ? sc.yMin : null,
            yMax: (sc.yMax || sc.yMax === 0) ? sc.yMax : null,
            pane: sc.pane ? sc.pane : 0,
            crosshairFormat: crosshairFormat,
            objectType: ObjectType.HistorianTag,
            renderingMode: sc.renderingMode,
            serieState: (sc.serieState as any) === true
                ? 1
                : (sc.serieState as any) === false
                    ? 0
                    : sc.serieState,
            decimals // For change detection purpose
        };
        return newSeries;
    }

    private createUnknownSeries(sc: SeriesConfiguration, name: string): any {
        const newSeries = {
            name: name,
            legendText: name,
            isVisible: false,
            identifier: sc.guid,
            identifierType: null,
            color: errorTextColor,
            value: (d: TagDateValue) => d.value,
            selector: (d: TagDateValue) => {
                let id = d.valueType;
                if (d.guid) {
                    id += d.guid.toLowerCase();
                }
                if (!!d.offset && !!d.offset.interval) {
                    id += (d.offset.interval.toString() + d.offset.intervalTimeUnit.toString());
                }
                return id;
            },
            symbol: '',
            isSymbolVisible: false,
            isLineVisible: false,
            isBarsVisible: false,
            isValueLabelsVisible: false,
            coloringConditions: [],
            hasOwnAxis: false,
            yMin: 0,
            yMax: 0,
            pane: 0,
            crosshairFormat: () => { },
            hasReconciledValue: false,
            isConstraint: false,
            attributes: null,
            objectType: ObjectType.Undefined,
            serieState: 0,
            lineStyle: null
        };
        return newSeries;
    }

    private getDecimals(decimals: number): number {
        return (!decimals && decimals !== 0)
            ? 2
            : decimals;
    }

    getMinMax(tagDefinition: TagDefinition, vm: IValueViewModel, groups: Dictionary<Group>): angular.IPromise<{ min: number; max: number; defaultValue: number }> {
        const result: any = {};
        if (tagDefinition.min !== null || tagDefinition.max !== null) {
            result.min = tagDefinition.min;
            result.max = tagDefinition.max;
            result.defaultValue = tagDefinition.defaultValue;
            return this.$q.when(result);
        } else {
            return this._groupService
                .getTagMinMax(tagDefinition.guid, vm, groups)
                .then(data => {
                    result.min = data.min;
                    result.max = data.max;
                    return result;
                });
        }
    }

    createMissingSerie(): any {
        const s = {
            name: missingId,
            identifier: missingId,
            color: '#999',
            selector: d => d.ID,
            value: [],
            isLineVisible: true,
            isValueLabelsVisible: false,
            seriesType: 'range',
            title: this._$translate.instant('Missing from _begin_ till _end_'),
            pane: 0,
            serieState: 0
        };
        return s;
    }

    updateMissingSeries(missing: KeyValuePair<string, any[]>[]): any[] {
        let result = [];
        if (missing && missing.length > 0) {
            const mapMissing = missing // flatten all values
                .map(kv => kv.value.map(m => ({ 'ID': missingId, begin: new Date(m.start), end: new Date(m.end) })))
                .reduce((ret, val) => ret.concat(val));
            result = mapMissing;
        }
        return result;
    }

    convertUnit(vm: IValueViewModel, configuration: IPresentationUnitConfiguration, variablesTypes: Dictionary<VariableType>): string {
        let result = vm.unitName;

        const hasConversion = configuration?.variableTypeGuid && configuration?.unitGuid;
        if (hasConversion && vm.variableTypeGuid === configuration.variableTypeGuid) {
            const vt = variablesTypes[configuration.variableTypeGuid];
            const unit = _.find(vt.unitCollection, u => u.guid === configuration.unitGuid);

            if (unit) {
                result = unit.name;
            }
        }

        return result;
    }
}